import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import { useTranslationApp } from "../../../lib/i18next";
import {
  CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
  CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
  CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
  CREATE_ADS_CAMPAIGN_TYPE_QUESTION,
  FIELD_REQUIRED,
  FIELDS_REQUIRED,
  CREATE_ADS_CAMPAIGN_GOOGLE_CONVERSION_ACTIONS_QUESTION,
  CREATE_ADS_CAMPAIGN_GOOGLE_CONVERSION_ACTIONS_DESCRIPTION,
  CREATE_ADS_CAMPAIGN_VIDEO_URLS_QUESTION,
  CREATE_ADS_CAMPAIGN_IMAGE_URLS_QUESTION,
  CREATE_ADS_CAMPAIGN_IMAGE_URLS_DESCRIPTION_QUESTION,
  BUTTON_SELECT_CONTENT,
  FIELD_VIDEO_YOUTUBE_URL_INVALID,
  CREATE_ADS_CAMPAIGN_MERCHANT_ID_QUESTION,
  REQUIRED_IMAGES_GOOGLE_SQUARE_IMAGE,
  REQUIRED_IMAGES_GOOGLE_HORIZONTAL_IMAGE,
  FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
  FIELD_URL_VALID_WITH_PROTOCOL,
  CREATE_ADS_CAMPAIGN_APP_ID_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_PRODUCTS_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_SERVICES_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT_SINGLE,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE_SINGLE,
  BUTTON_ADD_PRODUCT,
  BUTTON_ADD_SERVICE,
  PRODUCT_FIELDS_REQUIRED,
  SERVICE_FIELDS_REQUIRED,
  PRODUCT_URL_INVALID,
  SERVICE_URL_INVALID,
} from "../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION,
  ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
  GOOGLE,
  GOOGLE_IMAGE_FORMATS,
  ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION,
  ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION,
  BUSINESS_OFFERING_PHYSICAL_PRODUCT,
} from "../../../utils/constants";
import { useCreditActionCosts } from "../../useCreditActionCosts";
import { useDispatchApp } from "../../../lib/redux";
import {
  getCurrencyAdAccountPlatform,
  getIsBlockedActions,
} from "../../../actions/getters";
import _ from "lodash";
import IconButtonAddContent from "../../../components/Buttons/IconButtonAddContent";
import {
  formatMediaUrl,
  formatMediaUrlAdFromCreatify,
  getMinimumBudget,
} from "../../../services/adsCampaigns";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  REGEX_URL_WITH_PROTOCOL_REQUIRED,
  REGEX_YOUTUBE_URL,
} from "../../../utils/regex";
import {
  startGetCurrencyTax,
  startGetBusinessOffering,
} from "../../../actions/adsCampaigns";
import { usePlatformDefaultsInGenerateCampaign } from "../../platformDefaults/usePlatformDefaultsInGenerateCampaign";
import { useJumpToLastVisibleStep } from "../useJumpToLastVisibleStep";
import { saveInitialPlatformDefaultsFromCampaign } from "../../platformDefaults/saveInitialPlatformDefaultsFromCampaign";

const emptyMultipleItem = () => ({
  destinationUrl: "",
  imageUrls: [],
  videoUrls: [""],
});

const normalizeMultipleItemsForRequest = (items = []) =>
  items.map((item) => ({
    destinationUrl: item.destinationUrl || "",
    imageUrls: item.imageUrls || [],
    videoUrls: (item.videoUrls || [])
      .filter(Boolean)
      .map((videoUrl) =>
        typeof videoUrl === "string" ? { url: videoUrl } : videoUrl
      ),
  }));

export function useGenerateCampaignFormGoogle({
  campaignID,
  generatedPlatforms = {},
  language,
  campaignContext,
  onCallbackSave = () => {},
  onExitToPlatformSelection,
  verifyBlockAlert = true,
  resumeValues = null,
}) {
  const STEPS = [
    "objective",
    "identities",
    "appID",
    "merchantCenterID",
    "conversionActions",
    "destinationURL",
    "budget",
    "videoUrls",
    "imageUrls",
  ];
  const [error, setError] = useState(null);
  const [currencyTax, setCurrencyTax] = useState(1);
  const [businessOffering, setBusinessOffering] = useState(null);
  const formMethods = useForm({
    defaultValues: {
      step: 0,
      objective: ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
      template: ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION,
      integrationID: "",
      adAccountID: "",
      conversionActions: [],
      destinationURL: "",
      typeBudget: ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
      budget: "",
      startDate: new Date(),
      endDate: null,
      imageUrls: [],
      videoUrls: [""],
      multipleItems: [emptyMultipleItem()],
      appStore: "",
      appID: "",
    },
  });
  const { control, watch, setValue, getValues } = formMethods;

  const { isLoadingDefaults, resumeSettled } =
    usePlatformDefaultsInGenerateCampaign({
      platform: GOOGLE,
      setValue,
      getValues,
      resumeValues,
    });

  const step = watch("step");
  const objective = watch("objective");
  const template = watch("template");
  const typeBudget = watch("typeBudget");
  const integrationID = watch("integrationID");
  const adAccountID = watch("adAccountID");
  const imageUrls = watch("imageUrls");
  const multipleItems = watch("multipleItems");
  const utils = {
    ...formMethods,
    control,
    setValue,
    watch,
    getValues,
    campaignID,
    campaignContext,
    language,
    businessOffering,
  };

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();

  const { userID } = dispatch(getPathDatabaseByRole());

  const getCurrencyTax = async (currency) => {
    if (!currency) return;
    const { ok, data } = await dispatch(startGetCurrencyTax(currency));

    setCurrencyTax(ok ? data : 1);
  };

  const getInitialData = async () => {
    const response = await dispatch(startGetBusinessOffering());
    if (response.ok) {
      setBusinessOffering(response.data);
    } else {
      setBusinessOffering(null);
    }
  };

  const actionsToValidAnswer = () => {
    setError(null);
  };

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform: GOOGLE,
      integrationID: integrationID,
      adAccountID: adAccountID,
    })
  );

  const isPhysicalProduct =
    businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT;
  const isMultipleTemplate = template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION;

  const onFinished = async () => {
    const formValues = getValues();
    const isMultiple =
      formValues.template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION;

    if (!_.isEmpty(formValues.videoUrls)) {
      const videosFiltered = formValues.videoUrls.filter(Boolean);
      formValues.videoUrls = videosFiltered;
    }
    const requestObject = {
      objective: formValues.objective,
      template: formValues.template || ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION,
      integrationID: formValues.integrationID,
      adAccountID: formValues.adAccountID,
      conversionActions: formValues.conversionActions,
      destinationURL: isMultiple ? "" : formValues.destinationURL,
      typeBudget: formValues.typeBudget,
      budget: formValues.budget,
      startDate: _.isDate(formValues.startDate)
        ? new Date(formValues.startDate).getTime()
        : null,
      endDate: _.isDate(formValues.endDate)
        ? new Date(formValues.endDate).getTime()
        : null,
      imageUrls: isMultiple ? [] : formValues.imageUrls,
      videoUrls: isMultiple
        ? []
        : _.isEmpty(formValues.videoUrls)
          ? []
          : formValues.videoUrls.map((videoUrl) => ({
              url: videoUrl,
            })),
      multipleItems: isMultiple
        ? normalizeMultipleItemsForRequest(formValues.multipleItems)
        : [],
      merchantCenterID: formValues.merchantCenterID,
      appStore: formValues.appStore,
      appID: formValues.appID,
      platform: GOOGLE,
    };

    if (verifyBlockAlert) {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
          showUsageConfirmationAlert: true,
          campaignUsageContext: {
            platform: GOOGLE,
            generatedPlatforms,
          },
        })
      );
      if (isBlocked) {
        return;
      }
    }

    void saveInitialPlatformDefaultsFromCampaign({
      dispatch,
      platform: GOOGLE,
      formValues: getValues(),
    });

    onCallbackSave(requestObject);
  };

  useEffect(() => {
    if (currency) {
      getCurrencyTax(currency);
    }
    // eslint-disable-next-line
  }, [currency]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [userID]);

  const { steps, fields } = useMemo(() => {
    const fieldsConfig = {
      objective: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "objective",
        title: CREATE_ADS_CAMPAIGN_TYPE_QUESTION,
        showActionsButtons: true,
        showPreviousButton: Boolean(onExitToPlatformSelection),
        onPreviousStep: () => {
          actionsToValidAnswer();
          onExitToPlatformSelection?.();
        },
        showNextButton: true,
        onNextStep: () => {
          const objective = getValues("objective");
          if (!objective || objective?.length === 0) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      merchantCenterID: {
        isVisible: () => objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION,
        isFinishQuestion: false,
        utils,
        name: "merchantCenterID",
        title: CREATE_ADS_CAMPAIGN_MERCHANT_ID_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showSkipButton: true,
        onSkipStep: () => {
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
        showNextButton: true,
        onNextStep: () => {
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },

      conversionActions: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "conversionActions",
        title: CREATE_ADS_CAMPAIGN_GOOGLE_CONVERSION_ACTIONS_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_GOOGLE_CONVERSION_ACTIONS_DESCRIPTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showSkipButton: true,
        onSkipStep: () => {
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
        showNextButton: true,
        onNextStep: () => {
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      identities: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "identities",
        title: CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const integrationID = getValues("integrationID");
          const adAccountID = getValues("adAccountID");
          if (!adAccountID || !integrationID) {
            setError(t(FIELDS_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      appID: {
        isVisible: () => objective === ADS_CAMPAIGN_TYPE_APP_OPTION,
        isFinishQuestion: false,
        utils,
        name: "appID",
        title: CREATE_ADS_CAMPAIGN_APP_ID_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const appStore = getValues("appStore");
          if (!appStore) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          // Validate appID if appStore is selected
          const appID = getValues("appID");
          if (!appID) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      destinationURL: {
        isVisible: () =>
          objective !== ADS_CAMPAIGN_TYPE_APP_OPTION && !isMultipleTemplate,
        isFinishQuestion: false,
        utils,
        name: "destinationURL",
        title: CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const destinationURL = getValues("destinationURL");
          if (!destinationURL) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          if (
            !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(encodeURI(destinationURL))
          ) {
            setError(t(FIELD_URL_VALID_WITH_PROTOCOL));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      budget: {
        isVisible: () => true,
        isFinishQuestion: objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
        utils,
        name: "budget",
        title: CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton:
          objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION ||
          objective === ADS_CAMPAIGN_TYPE_APP_OPTION,
        onNextStep: () => {
          const budget = getValues("budget");
          if (!budget) {
            setError(t(FIELD_REQUIRED));
            return false;
          }

          // Validate minimum budget for Google (always daily)
          const minimumBudget = getMinimumBudget({
            platform: GOOGLE,
            typeBudget: ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
            currency,
            currencyTax,
          });

          if (minimumBudget && budget < minimumBudget) {
            setError(
              t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: minimumBudget,
                currency: currency,
              })
            );
            return false;
          }

          actionsToValidAnswer();
          setValue("step", step + 1);
        },
        onFinish: () => {
          const budget = getValues("budget");
          if (!budget) {
            setError(t(FIELD_REQUIRED));
            return false;
          }

          // Validate minimum budget for Google (always daily)
          const minimumBudget = getMinimumBudget({
            platform: GOOGLE,
            typeBudget: ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
            currency,
            currencyTax,
          });

          if (minimumBudget && budget < minimumBudget) {
            setError(
              t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: minimumBudget,
                currency: currency,
              })
            );
            return false;
          }

          actionsToValidAnswer();
          onFinished();
        },
      },
      imageUrls: {
        isVisible: () =>
          objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION ||
          objective === ADS_CAMPAIGN_TYPE_APP_OPTION,
        isFinishQuestion: true,
        disabledFinishButton:
          objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION &&
          (isMultipleTemplate
            ? !multipleItems ||
              multipleItems.length === 0 ||
              multipleItems.every(
                (item) =>
                  !item.destinationUrl ||
                  !item.imageUrls ||
                  item.imageUrls.length === 0
              )
            : !imageUrls || imageUrls.length === 0),
        utils,
        name: "imageUrls",
        title:
          objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION
            ? isPhysicalProduct
              ? CREATE_ADS_CAMPAIGN_MEDIA_URL_PRODUCTS_QUESTION
              : CREATE_ADS_CAMPAIGN_MEDIA_URL_SERVICES_QUESTION
            : CREATE_ADS_CAMPAIGN_IMAGE_URLS_QUESTION,
        subtitle:
          objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION
            ? isPhysicalProduct
              ? isMultipleTemplate
                ? CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT_SINGLE
                : CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_PRODUCT
              : isMultipleTemplate
                ? CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE_SINGLE
                : CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_SERVICE
            : CREATE_ADS_CAMPAIGN_IMAGE_URLS_DESCRIPTION_QUESTION,
        recommendedSizes: [
          REQUIRED_IMAGES_GOOGLE_SQUARE_IMAGE,
          REQUIRED_IMAGES_GOOGLE_HORIZONTAL_IMAGE,
        ],
        icon: <AddIcon />,
        showTitleButton:
          objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION &&
          ((imageUrls?.length === 0 &&
            template === ADS_CAMPAIGN_TEMPLATE_SINGULAR_OPTION) ||
            isMultipleTemplate),
        buttonTitleText: isPhysicalProduct
          ? t(BUTTON_ADD_PRODUCT)
          : t(BUTTON_ADD_SERVICE),
        onClickButtonTitle: () => {
          if (isMultipleTemplate) {
            const items = getValues("multipleItems") || [];
            setValue("multipleItems", [...items, emptyMultipleItem()]);
          } else {
            const currentDestination = getValues("destinationURL") || "";
            const currentImages = getValues("imageUrls") || [];
            const currentVideos = (getValues("videoUrls") || []).filter(
              Boolean
            );
            setValue("multipleItems", [
              {
                destinationUrl: currentDestination,
                imageUrls: currentImages,
                videoUrls: currentVideos.length > 0 ? currentVideos : [""],
              },
            ]);
            setValue("template", ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION);
            setValue("imageUrls", []);
            setValue("videoUrls", [""]);
            setValue("destinationURL", "");
            // destinationURL + videoUrls hide in multi — keep wizard on creatives step
            const multipleVisibleSteps = STEPS.filter((stepName) => {
              if (stepName === "appID") {
                return objective === ADS_CAMPAIGN_TYPE_APP_OPTION;
              }
              if (stepName === "merchantCenterID") {
                return objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION;
              }
              if (stepName === "destinationURL" || stepName === "videoUrls") {
                return false;
              }
              if (stepName === "imageUrls") {
                return (
                  objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION ||
                  objective === ADS_CAMPAIGN_TYPE_APP_OPTION
                );
              }
              return true;
            });
            const imageStepIndex = multipleVisibleSteps.indexOf("imageUrls");
            if (imageStepIndex >= 0) {
              setValue("step", imageStepIndex);
            }
          }
        },
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: false,
        customComponentPreviousNext: isMultipleTemplate ? null : (
          <Controller
            name="imageUrls"
            control={control}
            render={({ field }) => (
              <IconButtonAddContent
                mode="button"
                color="secondary"
                size="large"
                pathStorage={`adsCampaigns/${userID}/`}
                pathRTDB={`adsContentsUploaded/${userID}/${campaignID}`}
                modeUpload="sync"
                onAddContent={(contents) => {
                  const contentsFormatted = contents.map((content) =>
                    formatMediaUrl(content)
                  );
                  field.onChange([...field.value, ...contentsFormatted]);
                }}
                initialValues={{ language, campaignContext, platform: GOOGLE }}
                onCallbackSaveAI={(videoData) => {
                  field.onChange([
                    ...field.value,
                    formatMediaUrlAdFromCreatify(videoData),
                  ]);
                }}
                onCallbackSaveCanva={(mediaUrl) => {
                  const canvaContent = formatMediaUrl({
                    url: mediaUrl,
                    type: "upload",
                  });
                  field.onChange([...field.value, canvaContent]);
                }}
                showGalleryGoogleOption={true}
                showGenerateWithAIOption={true}
                multiple={true}
                acceptedFormats={GOOGLE_IMAGE_FORMATS}
                textButton={BUTTON_SELECT_CONTENT}
                sx={{
                  alignItems: "flex-start",
                  width: { xs: "100%", md: "auto" },
                }}
                sxButton={{
                  height: 48,
                  width: "100%",
                }}
                propsPostsAdsPlatforms={{
                  [GOOGLE]: {
                    integrationID: getValues("integrationID"),
                    adAccountID: getValues("adAccountID"),
                  },
                }}
              />
            )}
          />
        ),
        onFinish: () => {
          if (
            objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION &&
            isMultipleTemplate
          ) {
            const items = getValues("multipleItems");
            if (!items || items.length === 0) {
              setError(t(FIELD_REQUIRED));
              return false;
            }
            const invalidItemIndex = items.findIndex(
              (item) =>
                !item.destinationUrl ||
                !item.imageUrls ||
                item.imageUrls.length === 0
            );
            if (invalidItemIndex !== -1) {
              setError(
                t(
                  isPhysicalProduct
                    ? PRODUCT_FIELDS_REQUIRED
                    : SERVICE_FIELDS_REQUIRED,
                  { number: invalidItemIndex + 1 }
                )
              );
              return false;
            }
            const invalidUrlIndex = items.findIndex(
              (item) =>
                !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(
                  encodeURI(item.destinationUrl)
                )
            );
            if (invalidUrlIndex !== -1) {
              setError(
                t(
                  isPhysicalProduct ? PRODUCT_URL_INVALID : SERVICE_URL_INVALID,
                  { number: invalidUrlIndex + 1 }
                )
              );
              return false;
            }
            const invalidVideoIndex = items.findIndex((item) => {
              const videos = (item.videoUrls || []).filter(Boolean);
              return videos.some((videoUrl) => {
                const url =
                  typeof videoUrl === "string" ? videoUrl : videoUrl?.url;
                return url && !REGEX_YOUTUBE_URL.test(url);
              });
            });
            if (invalidVideoIndex !== -1) {
              setError(t(FIELD_VIDEO_YOUTUBE_URL_INVALID));
              return false;
            }
          } else if (objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION) {
            const imageUrls = getValues("imageUrls");
            if (!imageUrls || imageUrls?.length === 0) {
              setError(t(FIELD_REQUIRED));
              return false;
            }
          }
          // For App, images are optional
          actionsToValidAnswer();
          onFinished();
        },
      },
      videoUrls: {
        isVisible: () =>
          (objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION ||
            objective === ADS_CAMPAIGN_TYPE_APP_OPTION) &&
          !isMultipleTemplate,
        isFinishQuestion: false,
        utils,
        name: "videoUrls",
        title: CREATE_ADS_CAMPAIGN_VIDEO_URLS_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showSkipButton: true,
        onSkipStep: () => {
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const videoUrls = getValues("videoUrls");
          // Videos are optional, but if provided, they must be valid YouTube URLs
          if (videoUrls && videoUrls.length > 0) {
            const videosFiltered = videoUrls.filter(Boolean);
            if (videosFiltered.length > 0) {
              const someVideoUrlIsInvalid = videosFiltered.some((videoUrl) => {
                return !REGEX_YOUTUBE_URL.test(videoUrl);
              });
              if (someVideoUrlIsInvalid) {
                setError(t(FIELD_VIDEO_YOUTUBE_URL_INVALID));
                return false;
              }
            }
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
    };

    const stepsVisible = STEPS.filter((step) => fieldsConfig[step].isVisible());

    return {
      steps: stepsVisible,
      fields: fieldsConfig,
    };

    // eslint-disable-next-line
  }, [
    step,
    objective,
    typeBudget,
    imageUrls,
    template,
    multipleItems,
    businessOffering,
    onExitToPlatformSelection,
  ]);

  const { hasJumped } = useJumpToLastVisibleStep({
    enabled: Boolean(resumeValues),
    ready: resumeSettled,
    steps,
    setValue,
  });
  const isApplyingResume = Boolean(resumeValues) && !hasJumped;
  const currentAnswer = isApplyingResume ? null : steps[step];
  const totalSteps = steps.length;
  const progress = Math.round(((step + 1) / totalSteps) * 100);

  return {
    steps,
    fields,
    utils,
    currentAnswer,
    error,
    progress,
    currency,
    currencyTax,
    isLoadingDefaults,
  };
}
